import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { getDocumentPath, setDocumentsView } from 'redux/actions/documentActions';
import { saveAlert } from 'redux/actions/notificationActions';
import useDocumentation from 'hooks/useDocumentation';
import { colors } from 'shared/defaultTheme';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import Contacts from 'shared/components/DocumentForm/Contacts';
import AlertModal from 'shared/components/AlertModal';
import MainSpinner from 'shared/components/MainSpinner';
import { ContactsModal } from 'shared/components/SharedStyles/SharedStyles';
import ConfirmModal from 'shared/components/ConfirmModal';

import { DOCUMENTS_VIEW } from 'shared/constants/variants';
import FileManager from './FileManager';
import NotificationForm from './NotificationForm';
import { DocumentWrapper, DocumentTableBackground, FormModal } from '../styles';

const Documentations = ({ clientId, refFileManager, setTotalSizeUpload, totalSizeUpload }) => {
  const dispatch = useDispatch();
  const documentState = useSelector((state) => state.document);
  const notificationState = useSelector((state) => state.notification);
  const { t } = useTranslation();
  const refMenu = useRef(null);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [refreshFolder, setRefreshFolder] = useState(false);
  const [path, setPath] = useState();
  const [showError, setShowError] = useState(false);
  const [messageErrorDownload, setMessageError] = useState('');
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [loadingCreateFolder, setLoadingCreateFolder] = useState(false);
  const [showConfirmUnPublic, setShowConfirmUnPublic] = useState(false);
  const [showConfirmUnPublicFolder, setShowConfirmUnPublicFolder] = useState(false);
  const [isInputFile, setInputFile] = useState(false);

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const {
    handlePopUpClose,
    handleMenuClick,
    elementEmptyTable,
    elementEmptyLargeIcon,
    handleToolbarCreate,
    handleActionAddClick,
    loadingDownload,
    handleToolbarClick,
    handlePopupOpen,
    handleMenuOpen,
    onOkUnPublic,
    loadingUnPublic,
    onCloseContactsModal,
    onCancelAction,
    onCloseFormModal,
  } = useDocumentation({
    refFileManager,
    setOpenFormModal,
    setOpenContactsModal,
    setShowConfirmUnPublic,
    setShowConfirmUnPublicFolder,
    setMessageError,
    onToggleError,
    setInputFile,
  });

  useEffect(() => {
    dispatch(setDocumentsView(DOCUMENTS_VIEW.CLIENTS));
    return () => {
      dispatch(setDocumentsView(''));
    };
  }, []);

  useEffect(() => {
    const fileManager = refFileManager;
    if (documentState.path && documentState.readDocument) {
      fileManager.current.path = documentState.path;
      dispatch(getDocumentPath(null));
    }
    if (documentState.readDocument && path) {
      fileManager.current.path = path;
      setPath(null);
    }
  }, [documentState.readDocument, path]);

  useEffect(() => {
    if (notificationState.isSaveAlert) {
      const fileManager = refFileManager;
      fileManager.current.refresh();
      dispatch(saveAlert(false));
    }
  }, [notificationState.isSaveAlert]);

  useEffect(() => {
    if (documentState.failure) {
      const content = document?.querySelector('.e-dlg-body-error');
      const messageError = content?.querySelector('.e-fe-errorcontent');
      const { action, error } = documentState.failure;
      if (action === 'read' && error.code === '404' && messageError) {
        messageError.innerHTML = t('documents_not_found_client');
      }
    }
  }, [documentState.failure]);

  useEffect(() => {
    if (documentState.readDocument) {
      setTimeout(() => setRefreshFolder(true), 500);
    }
  }, [documentState.readDocument]);

  useEffect(() => {
    if (refreshFolder) {
      setRefreshFolder(false);
      elementEmptyTable();
      elementEmptyLargeIcon();
    }
  }, [refreshFolder]);

  return (
    <DocumentWrapper className="pb-3">
      <DocumentTableBackground className="mr-2 background-box__flex-documentations">
        {clientId && (
          <FileManager
            clientId={clientId}
            onToolbarCreate={handleToolbarCreate}
            onToolbarClick={handleToolbarClick}
            onMenuClick={handleMenuClick}
            onMenuOpen={handleMenuOpen}
            onActionAddClick={handleActionAddClick}
            onPopupOpen={handlePopupOpen}
            onPopUpClose={handlePopUpClose}
            refMenu={refMenu}
            refFileManager={refFileManager}
            setLoadingCreateFolder={setLoadingCreateFolder}
            loadingCreateFolder={loadingCreateFolder}
            totalSizeUpload={totalSizeUpload}
            setTotalSizeUpload={setTotalSizeUpload}
            isInputFile={isInputFile}
            setInputFile={setInputFile}
          />
        )}
      </DocumentTableBackground>
      <FormModal isOpen={openFormModal} toggle={onCloseFormModal} size="md">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFormModal}
          clientId={clientId}
        />
        <ModalBody>
          <NotificationForm onCancel={onCloseFormModal} clientId={clientId} />
        </ModalBody>
      </FormModal>
      <ContactsModal isOpen={openContactsModal} toggle={onCloseContactsModal} size="md">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseContactsModal}
        />
        <ModalBody>
          <Contacts onCancel={onCloseContactsModal} />
        </ModalBody>
      </ContactsModal>
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={messageErrorDownload}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      <ConfirmModal
        isOpen={showConfirmUnPublic}
        toggle={onCancelAction}
        confirmMessage={t('document_un_public_confirm')}
        cancelText={t('general_discard')}
        okText={t('general_button_ok')}
        onCancel={onCancelAction}
        onOk={onOkUnPublic}
      />
      <ConfirmModal
        isOpen={showConfirmUnPublicFolder}
        toggle={onCancelAction}
        confirmMessage={t('folder_un_public_confirm')}
        cancelText={t('general_discard')}
        okText={t('general_button_ok')}
        onCancel={onCancelAction}
        onOk={onOkUnPublic}
      />
      {(loadingDownload || loadingCreateFolder || loadingUnPublic) && <MainSpinner />}
    </DocumentWrapper>
  );
};

Documentations.defaultProps = {
  refFileManager: null,
};

Documentations.propTypes = {
  clientId: PropTypes.string.isRequired,
  refFileManager: PropTypes.objectOf(PropTypes.shape()),
  setTotalSizeUpload: PropTypes.func.isRequired,
  totalSizeUpload: PropTypes.number.isRequired,
};

export default Documentations;
